import { IconAlertCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { Lang } from "../api";
import { STATUS_KEYS, translate } from "../i18n";
import { useSession } from "../session";

export function Card({ children, className = "", onClick, href, id }: { children: ReactNode; className?: string; onClick?: () => void; href?: string; id?: string }) {
  if (href) return <a href={href} id={id} className={`card card-link ${className}`}>{children}</a>;
  if (onClick) return <button type="button" id={id} className={`card card-link ${className}`} onClick={onClick}>{children}</button>;
  return <section id={id} className={`card ${className}`}>{children}</section>;
}

export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="section-title">
      <h2>{children}</h2>
      {aside}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useSession();
  return (
    <div className="spinner-wrap" role="status">
      <span className="spinner" />
      <span className="muted">{label ?? t("loading")}</span>
    </div>
  );
}

export function ErrorNote({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  const { t } = useSession();
  return (
    <div className="error-note" role="alert">
      <IconAlertCircle size={18} />
      <span>{children}</span>
      {onRetry && (
        <button type="button" className="link-button" onClick={onRetry}>
          {t("retry")}
        </button>
      )}
    </div>
  );
}

export function StatusChip({ status, lang }: { status: string; lang: Lang }) {
  const key = STATUS_KEYS[status];
  return <span className={`chip chip-${status}`}>{key ? translate(lang, key) : status}</span>;
}

export function Empty({ title, body }: { title: string; body?: string }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {body && <p className="muted">{body}</p>}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}
